import clsx from "clsx";
import {
  ALGOLIA_ASSESSMENT_ICD_CODES,
  DOCS_FORM_NAMES,
  FUNCTION_DIAGNOSIS_PRIORITIES,
  MUSCOSKELETAL_RESILIENCE,
  NUMBER_OF_OPS_FIELDS,
  OPERATION_INDEXES,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_UNKNOWN,
  PREDICAMENT_STATE_YES,
  REHAB_ADMISSION,
  REHAB_DIRECTIVE_TYPE,
  REHAB_FOCUS,
  SPECIAL_REQUIREMENTS,
  TRANSPORTATION_SUBTYPE,
  TRANSPORTATION_TYPE,
  TYPE_OF_REHAB,
} from "core/consts";
import { INFECTIONS_CHARACTER_LIMIT } from "core/schemas/shared";
import { isFilled } from "core/schemas/utils";
import { GKVMedicalFormData, PredicamentYesNo } from "core/types";
import {
  Button,
  ControlledDatePicker,
  ControlledInput,
  ControlledNumberInput,
  ControlledRadioGroup,
  ControlledSelect,
  ControlledTextarea,
  Input,
  RadioGroupProps,
} from "ds/ui";
import { ControlledAlgoliaSelect } from "dsl/atoms/AlgoliaSelect/AlgoliaSelectControlled";
import { renderIcdHit } from "dsl/atoms/IcdCodesSelect";
import {
  SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS,
  useICDReasonOptions,
  useSideLocalizationOptions,
} from "dsl/ecosystems/PatientForms/RehabForms/GKVMedicalForm/hooks";
import { useVisibleCount } from "dsl/hooks";
import { kebabCase } from "lodash";
import { PlusIcon, TrashIcon } from "lucide-react";
import {
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { DEFAULT_OPS } from ".";
import { FormFieldConfigProps } from "..";
import {
  ControlledCheckboxGroupWithFocus,
  ControlledRadioGroupWithFocus,
  SourceWrapper,
} from "../../utils";

export const RehabAdmissionTypeField = ({
  formName,
  skipRequiredValidation,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const { setValue } = useFormContext<GKVMedicalFormData>();
  const rehabAdmissionType = useWatch({ name: "rehab_admission_type" });

  useEffect(() => {
    if (rehabAdmissionType !== REHAB_ADMISSION.OUTSIDE_TWO_WEEKS) {
      setValue("rehab_admission_outside_two_weeks_explanation", "");
      setValue("rehab_admission_outside_two_weeks_start_date", null);
    }
    if (rehabAdmissionType !== REHAB_ADMISSION.WITHIN_TWO_WEEKS) {
      setValue("rehab_admission_within_two_weeks_start_date", null);
    }
  }, [rehabAdmissionType, setValue]);

  const options = [
    {
      label: translations.patientForms.medicalForm.sectionOne.optionDirect,
      value: REHAB_ADMISSION.DIRECT,
    },
    {
      label: translations.patientForms.medicalForm.sectionOne.withinTwoWeeks,
      subForm: () => (
        <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
          fieldName="rehab_admission_within_two_weeks_start_date"
          formName={formName}
          isIconOnly
          sourceInformation={sourceInformation}
        >
          <ControlledDatePicker
            label={
              translations.patientForms.medicalForm.sectionOne
                .earliestDatePossible
            }
            name="rehab_admission_within_two_weeks_start_date"
          />
        </SourceWrapper>
      ),
      value: REHAB_ADMISSION.WITHIN_TWO_WEEKS,
    },
    {
      label: translations.patientForms.medicalForm.sectionOne.outsideTwoWeeks,
      subForm: () => (
        <>
          <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
            fieldName="rehab_admission_outside_two_weeks_start_date"
            formName={formName}
            isIconOnly
            sourceInformation={sourceInformation}
          >
            <ControlledDatePicker
              label={
                translations.patientForms.medicalForm.sectionOne
                  .earliestDatePossible
              }
              name="rehab_admission_outside_two_weeks_start_date"
            />
          </SourceWrapper>
          <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
            fieldName="rehab_admission_outside_two_weeks_explanation"
            formName={formName}
            isIconOnly
            sourceInformation={sourceInformation}
          >
            <ControlledTextarea
              label={
                translations.patientForms.medicalForm.sectionOne.medicalReasons
              }
              name="rehab_admission_outside_two_weeks_explanation"
            />
          </SourceWrapper>
        </>
      ),
      value: REHAB_ADMISSION.OUTSIDE_TWO_WEEKS,
    },
  ];

  return (
    <ControlledRadioGroupWithFocus
      aria-label={
        translations.patientForms.generalForm.sectionI.accessibilityGuardianType
      }
      isRequired={!skipRequiredValidation}
      name="rehab_admission_type"
      options={options}
      size="md"
    />
  );
};

export const InfectionsStateField = ({
  formName,
  skipRequiredValidation,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const { setValue } = useFormContext<GKVMedicalFormData>();
  const infectionsState = useWatch({ name: "infections_state" });

  useEffect(() => {
    if (infectionsState !== PREDICAMENT_STATE_YES) {
      setValue("infections", "");
    }
  }, [infectionsState, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
          fieldName="infections"
          formName={formName}
          isIconOnly
          sourceInformation={sourceInformation}
        >
          <ControlledTextarea
            label={translations.patientForms.medicalForm.sectionTwo.typeOfGerms}
            maxLength={INFECTIONS_CHARACTER_LIMIT}
            name="infections"
          />
        </SourceWrapper>
      ),
      value: PREDICAMENT_STATE_YES,
    },
    {
      label: translations.patientForms.medicalForm.sectionTwo.germsUnknown,
      value: PREDICAMENT_STATE_UNKNOWN,
    },
  ];

  return (
    <ControlledRadioGroupWithFocus
      className="w-full"
      isRequired={!skipRequiredValidation}
      label={translations.patientForms.medicalForm.sectionTwo.germs}
      name="infections_state"
      options={options}
      size="md"
    />
  );
};

export const ComplicationInTreatmentProcessField = ({
  auction,
  formName,
  skipRequiredValidation,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const { setValue } = useFormContext<GKVMedicalFormData>();
  const complicationInTreatmentProcess = useWatch({
    name: "complication_in_treatment_process",
  });

  useEffect(() => {
    if (complicationInTreatmentProcess !== PREDICAMENT_STATE_YES) {
      setValue("complication_cause_maindiagnosis_description", "");
      setValue("complication_cause_cardiovascular_description", "");
      setValue("complication_cause_other_description", "");
    }
  }, [complicationInTreatmentProcess, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <ComplicationsSubForm
          auction={auction}
          formName={formName}
          skipRequiredValidation={skipRequiredValidation}
          sourceInformation={sourceInformation}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroupWithFocus
      aria-label={
        translations.patientForms.medicalForm.sectionTwo.complications
      }
      className="w-full"
      isRequired={!skipRequiredValidation}
      name="complication_in_treatment_process"
      options={options}
      size="md"
    />
  );
};

const ComplicationsSubForm = ({
  formName,
  skipRequiredValidation,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const complicationCauseMaindiagnosisDescription =
    useWatch<GKVMedicalFormData>({
      name: "complication_cause_maindiagnosis_description",
    });
  const complicationCauseCardiovascularDescription =
    useWatch<GKVMedicalFormData>({
      name: "complication_cause_cardiovascular_description",
    });
  const complicationCauseOtherDescription = useWatch<GKVMedicalFormData>({
    name: "complication_cause_other_description",
  });

  const complicationOptions = [
    {
      label:
        translations.patientForms.medicalForm.sectionTwo.relatedToDiagnosis,
      name: "complication_cause_maindiagnosis_description",
    },
    {
      label:
        translations.patientForms.medicalForm.sectionTwo.optionCardiovascular,
      name: "complication_cause_cardiovascular_description",
    },
    {
      label:
        translations.patientForms.medicalForm.sectionTwo
          .optionOtherComplication,
      name: "complication_cause_other_description",
    },
  ] as const;

  const allEmpty =
    !isFilled(complicationCauseMaindiagnosisDescription) &&
    !isFilled(complicationCauseCardiovascularDescription) &&
    !isFilled(complicationCauseOtherDescription);

  const isFieldFilled = (
    fieldName: (typeof complicationOptions)[number]["name"],
  ) => {
    switch (fieldName) {
      case "complication_cause_maindiagnosis_description":
        return isFilled(complicationCauseMaindiagnosisDescription);
      case "complication_cause_cardiovascular_description":
        return isFilled(complicationCauseCardiovascularDescription);
      case "complication_cause_other_description":
        return isFilled(complicationCauseOtherDescription);
    }
  };

  return (
    <>
      {complicationOptions.map(({ label, name }) => {
        const isRequired =
          !skipRequiredValidation && (allEmpty || isFieldFilled(name));

        return (
          <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
            key={name}
            fieldName={name as keyof GKVMedicalFormData}
            formName={formName}
            isIconOnly
            sourceInformation={sourceInformation}
          >
            <ControlledTextarea
              isRequired={isRequired}
              label={label}
              name={name}
            />
          </SourceWrapper>
        );
      })}
    </>
  );
};

export const RehaCapacityField = ({
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const { setValue } = useFormContext<GKVMedicalFormData>();
  const rehaCapacity = useWatch({ name: "reha_capacity" });

  useEffect(() => {
    if (rehaCapacity !== MUSCOSKELETAL_RESILIENCE.PARTIALLY) {
      setValue("reha_capacity_partial", "");
    }
  }, [rehaCapacity, setValue]);

  const options = [
    {
      label: translations.patientForms.medicalForm.sectionThree.fully,
      value: MUSCOSKELETAL_RESILIENCE.FULLY,
    },
    {
      label: translations.patientForms.medicalForm.sectionThree.partially,
      subForm: () => (
        <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
          fieldName="reha_capacity_partial"
          formName={formName}
          isIconOnly
          sourceInformation={sourceInformation}
        >
          <ControlledNumberInput
            name="reha_capacity_partial"
            placeholder={translations.abbreviations.kilogram.abbreviation}
          />
        </SourceWrapper>
      ),
      value: MUSCOSKELETAL_RESILIENCE.PARTIALLY,
    },
    {
      label: translations.patientForms.medicalForm.sectionThree.stable,
      value: MUSCOSKELETAL_RESILIENCE.STABLE,
    },
  ];

  return (
    <ControlledRadioGroup
      aria-label={
        translations.patientForms.medicalForm.sectionThree.musculoskeletalSystem
      }
      name="reha_capacity"
      options={options}
      size="md"
    />
  );
};

export const RehabFocusField = ({
  formName,
  skipRequiredValidation,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const { setValue } = useFormContext<GKVMedicalFormData>();
  const rehabFocus = useWatch({ name: "rehab_focus" });

  useEffect(() => {
    if (rehabFocus !== REHAB_FOCUS.OTHER) {
      setValue("rehab_focus_other_description", "");
    }
  }, [rehabFocus, setValue]);

  const options = [
    {
      label: translations.patientForms.medicalForm.sectionFive.heart,
      value: REHAB_FOCUS.HEART,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.neurologicalDisease,
      value: REHAB_FOCUS.NEUROLOGICAL_DISEASE,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.bloodVessels,
      value: REHAB_FOCUS.BLOOD_VESSELS,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.oncologicalDiseases,
      value: REHAB_FOCUS.ONCOLOGICAL_DISEASE,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.inflammatoryDiseases,
      value: REHAB_FOCUS.INFLAMMATORY_DISEASE,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive
          .gynaecologicalDiseases,
      value: REHAB_FOCUS.GYNAECOLOGICAL_DISEASE,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.musculoskeletalSystem,
      value: REHAB_FOCUS.MUSCOLOSKELETAL_DISEASE,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.skinDiseases,
      value: REHAB_FOCUS.SKIN_DISEASE,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive
          .gastroenterologicalDiseases,
      value: REHAB_FOCUS.GASTROENTEROLOGICAL_DISEASE,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive
          .psychosomaticDisorders,
      value: REHAB_FOCUS.PSYCHOSOMATIC_DISORDER,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.endocrineDiseases,
      value: REHAB_FOCUS.ENDOCRINE_DISEASE,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.mentalIllnesses,
      value: REHAB_FOCUS.MENTAL_ILLNESS,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.respiratoryDiseases,
      value: REHAB_FOCUS.RESPIRATORY_DISEASE,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.addictiveDisorders,
      value: REHAB_FOCUS.ADDICTIVE_DISORDER,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.nephrologicalDiseases,
      value: REHAB_FOCUS.NEPHROLOGICAL_DISEASE,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.bloodDiseases,
      value: REHAB_FOCUS.BLOOD_DISEASE,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.urologicalDiseases,
      value: REHAB_FOCUS.UROLOGICAL_DISEASE,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.geriatrics,
      value: REHAB_FOCUS.GERIATRICS,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.others,
      subForm: () => (
        <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
          fieldName="rehab_focus_other_description"
          formName={formName}
          isIconOnly
          sourceInformation={sourceInformation}
        >
          <ControlledTextarea
            label={
              translations.patientForms.medicalForm.sectionFive
                .otherTextfieldLabel
            }
            name="rehab_focus_other_description"
          />
        </SourceWrapper>
      ),
      value: REHAB_FOCUS.OTHER,
    },
  ];

  return (
    <ControlledRadioGroupWithFocus
      aria-label={translations.patientForms.medicalForm.sectionFive.rehabFocus}
      isRequired={!skipRequiredValidation}
      name="rehab_focus"
      options={options}
      size="md"
    />
  );
};

export const RecommendedRehaTypeField = ({
  formName,
  skipRequiredValidation,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const { setValue } = useFormContext<GKVMedicalFormData>();
  const recommendedRehaType = useWatch({ name: "recommended_reha_type" });

  useEffect(() => {
    if (
      ![TYPE_OF_REHAB.AMBULANT_MOBIL, TYPE_OF_REHAB.STATIONARY].includes(
        recommendedRehaType,
      )
    ) {
      setValue("recommended_reha_type_reason", "", { shouldValidate: true });
    }
  }, [recommendedRehaType, setValue]);

  const options = [
    {
      label: translations.patientForms.medicalForm.sectionFive.mobile,
      value: TYPE_OF_REHAB.AMBULANT,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.mobileOutpatient,
      value: TYPE_OF_REHAB.AMBULANT_MOBIL,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.static,
      value: TYPE_OF_REHAB.STATIONARY,
    },
  ];

  const isRequired = [
    TYPE_OF_REHAB.AMBULANT_MOBIL,
    TYPE_OF_REHAB.STATIONARY,
  ].includes(recommendedRehaType);

  return (
    <>
      <ControlledRadioGroupWithFocus
        aria-label={
          translations.patientForms.medicalForm.sectionFive.typeOfRehab
        }
        isRequired={!skipRequiredValidation}
        name="recommended_reha_type"
        options={options}
        size="md"
      />
      {isRequired && (
        <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
          fieldName="recommended_reha_type_reason"
          formName={formName}
          isIconOnly
          sourceInformation={sourceInformation}
        >
          <ControlledTextarea
            isRequired={isRequired && !skipRequiredValidation}
            label={
              translations.patientForms.medicalForm.sectionTwo
                .complicationsDescriptionLabel
            }
            name="recommended_reha_type_reason"
          />
        </SourceWrapper>
      )}
    </>
  );
};

export const RehabDirectiveStateField = ({
  auction,
  formName,
  skipRequiredValidation,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const { setValue } = useFormContext<GKVMedicalFormData>();
  const rehabDirectiveState = useWatch({ name: "rehab_directive_state" });

  useEffect(() => {
    if (rehabDirectiveState !== PREDICAMENT_STATE_YES) {
      setValue("rehab_directive_type", null, { shouldValidate: true });
    }
  }, [rehabDirectiveState, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <RehabDirectiveTypeSubForm
          auction={auction}
          formName={formName}
          skipRequiredValidation={skipRequiredValidation}
          sourceInformation={sourceInformation}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroupWithFocus
      aria-label={translations.patientForms.medicalForm.sectionFive.rehabRl}
      isRequired={!skipRequiredValidation}
      name="rehab_directive_state"
      options={options}
      size="md"
    />
  );
};

const RehabDirectiveTypeSubForm = ({
  auction: _auction,
  formName: _formName,
  skipRequiredValidation,
  sourceInformation: _sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const options = [
    {
      label:
        translations.patientForms.medicalForm.sectionFive.myocardialInfarction,
      value: REHAB_DIRECTIVE_TYPE.MYOCARDIAL_INFARCTION,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.bypass,
      value: REHAB_DIRECTIVE_TYPE.BYPASS_WITH_VALVE_REPLACEMENT,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.heartFailure,
      value: REHAB_DIRECTIVE_TYPE.HEART_FAILURE_NYHA_II_OR_HIGHER,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive
          .endoprostheticTreatment,
      value: REHAB_DIRECTIVE_TYPE.ENDOPROSTHETIC_KNEE_OR_HIP,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.backPain,
      value: REHAB_DIRECTIVE_TYPE.CHRONIC_BACK_PAIN,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.spinalCanal,
      value: REHAB_DIRECTIVE_TYPE.SPINAL_STENOSIS,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.amputation,
      value: REHAB_DIRECTIVE_TYPE.MAJOR_LEG_AMPUTATION,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.respiratoryDisease,
      value: REHAB_DIRECTIVE_TYPE.RESPIRATORY_DISEASES,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.neurologicalDiseases,
      value: REHAB_DIRECTIVE_TYPE.NEUROLOGICAL_DISEASE_PHASE_D,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionFive.oncologicalDisease,
      value: REHAB_DIRECTIVE_TYPE.ONCOLOGICAL_DISEASES,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.transplants,
      value: REHAB_DIRECTIVE_TYPE.ORGAN_TRANSPLANTS,
    },
    {
      label: translations.patientForms.medicalForm.sectionFive.geriatricRehab,
      value: REHAB_DIRECTIVE_TYPE.GERIATRIC_REHAB,
    },
  ];

  return (
    <ControlledRadioGroupWithFocus
      aria-label={
        translations.patientForms.medicalForm.sectionFive
          .rehabDirectiveTypeLabel
      }
      isRequired={!skipRequiredValidation}
      name="rehab_directive_type"
      options={options}
      size="md"
    />
  );
};

export const SpecialRequirementsRequiredField = ({
  auction,
  formName,
  skipRequiredValidation,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const { setValue } = useFormContext<GKVMedicalFormData>();
  const specialRequirementsRequired = useWatch({
    name: "special_requirements_required",
  });

  useEffect(() => {
    if (specialRequirementsRequired !== PREDICAMENT_STATE_YES) {
      setValue("special_requirements", undefined);
      Object.values(SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS).forEach(
        (fieldName) => {
          setValue(fieldName as keyof GKVMedicalFormData, "");
        },
      );
    }
  }, [specialRequirementsRequired, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <SpecialRequirementsSubForm
          auction={auction}
          formName={formName}
          skipRequiredValidation={skipRequiredValidation}
          sourceInformation={sourceInformation}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroupWithFocus
      aria-label={
        translations.patientForms.medicalForm.sectionSix.specialRequirements
      }
      isRequired={!skipRequiredValidation}
      name="special_requirements_required"
      options={options}
      size="md"
    />
  );
};

const SpecialRequirementsSubForm = ({
  formName,
  skipRequiredValidation,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const { setValue } = useFormContext<GKVMedicalFormData>();
  const specialRequirements = useWatch({ name: "special_requirements" });
  const specialRequirementsKey = JSON.stringify(specialRequirements ?? []);

  useEffect(() => {
    if (!Array.isArray(specialRequirements)) return;

    Object.entries(SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS).forEach(
      ([value, fieldName]) => {
        if (!specialRequirements.includes(Number(value))) {
          setValue(fieldName as keyof GKVMedicalFormData, "");
        }
      },
    );
  }, [specialRequirementsKey, setValue]);

  const options = [
    {
      label: translations.patientForms.medicalForm.sectionSix.chemotherapy,
      value: SPECIAL_REQUIREMENTS.CHEMOTHERAPY,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionSix.immunosuppressants,
      value: SPECIAL_REQUIREMENTS.IMMUNOSUPPRESSANTS,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.catheter,
      value: SPECIAL_REQUIREMENTS.CATHETER,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.peg,
      value: SPECIAL_REQUIREMENTS.PEG,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.isolation,
      value: SPECIAL_REQUIREMENTS.ISOLATION,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.ventilation,
      value: SPECIAL_REQUIREMENTS.VENTILATION,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.tracheostoma,
      value: SPECIAL_REQUIREMENTS.TRACHEOSTOMA,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.weaning,
      value: SPECIAL_REQUIREMENTS.WEANING,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.prosthesis,
      value: SPECIAL_REQUIREMENTS.PROSTHESIS,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.impairment,
      value: SPECIAL_REQUIREMENTS.IMPAIRMENT,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.relatives,
      subForm: () => (
        <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
          fieldName={
            SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS[
              SPECIAL_REQUIREMENTS.RELATIVES
            ] as keyof GKVMedicalFormData
          }
          formName={formName}
          isIconOnly
          sourceInformation={sourceInformation}
        >
          <ControlledTextarea
            label={
              translations.patientForms.medicalForm.sectionFive
                .otherTextfieldLabel
            }
            name={
              SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS[
                SPECIAL_REQUIREMENTS.RELATIVES
              ] ?? ""
            }
          />
        </SourceWrapper>
      ),
      value: SPECIAL_REQUIREMENTS.RELATIVES,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.dialysis,
      subForm: () => (
        <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
          fieldName={
            SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS[
              SPECIAL_REQUIREMENTS.DIALYSIS
            ] as keyof GKVMedicalFormData
          }
          formName={formName}
          isIconOnly
          sourceInformation={sourceInformation}
        >
          <ControlledTextarea
            label={
              translations.patientForms.medicalForm.sectionFive
                .otherTextfieldLabel
            }
            name={
              SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS[
                SPECIAL_REQUIREMENTS.DIALYSIS
              ] ?? ""
            }
          />
        </SourceWrapper>
      ),
      value: SPECIAL_REQUIREMENTS.DIALYSIS,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.decubitus,
      subForm: () => (
        <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
          fieldName={
            SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS[
              SPECIAL_REQUIREMENTS.DECUBITUS
            ] as keyof GKVMedicalFormData
          }
          formName={formName}
          isIconOnly
          sourceInformation={sourceInformation}
        >
          <ControlledTextarea
            label={
              translations.patientForms.medicalForm.sectionFive
                .otherTextfieldLabel
            }
            name={
              SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS[
                SPECIAL_REQUIREMENTS.DECUBITUS
              ] ?? ""
            }
          />
        </SourceWrapper>
      ),
      value: SPECIAL_REQUIREMENTS.DECUBITUS,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.other,
      subForm: () => (
        <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
          fieldName={
            SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS[
              SPECIAL_REQUIREMENTS.OTHER
            ] as keyof GKVMedicalFormData
          }
          formName={formName}
          isIconOnly
          sourceInformation={sourceInformation}
        >
          <ControlledTextarea
            label={
              translations.patientForms.medicalForm.sectionFive
                .otherTextfieldLabel
            }
            name={
              SPECIAL_REQUIREMENTS_DESCRIPTION_FIELDS[
                SPECIAL_REQUIREMENTS.OTHER
              ] ?? ""
            }
          />
        </SourceWrapper>
      ),
      value: SPECIAL_REQUIREMENTS.OTHER,
    },
  ];

  return (
    <ControlledCheckboxGroupWithFocus
      aria-label={
        translations.patientForms.medicalForm.sectionSix
          .specialRequirementsSelectOptionsLabel
      }
      isRequired={!skipRequiredValidation}
      name="special_requirements"
      options={options}
    />
  );
};

export const TransportTypeField = ({
  formName,
  skipRequiredValidation,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const { setValue } = useFormContext<GKVMedicalFormData>();
  const transportType = useWatch({ name: "transport_type" });

  useEffect(() => {
    if (transportType !== TRANSPORTATION_TYPE.TAXI) {
      setValue("transport_type_taxi_specification", null);
    }
    if (transportType !== TRANSPORTATION_TYPE.AMBULANCE) {
      setValue("transport_type_ambulance_specification", null);
      setValue("transport_type_ambulance_description", "");
    }
  }, [transportType, setValue]);

  const taxiOptions = [
    {
      label: translations.patientForms.medicalForm.sectionSix.wheelChair,
      value: TRANSPORTATION_SUBTYPE.WHEELCHAIR,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.stretcher,
      value: TRANSPORTATION_SUBTYPE.STRETCHER,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.lyingDown,
      value: TRANSPORTATION_SUBTYPE.LYING_DOWN,
    },
  ];

  const options = [
    {
      label: translations.patientForms.medicalForm.sectionSix.mobileRehab,
      value: TRANSPORTATION_TYPE.MOBILE_REHAB,
    },
    {
      label:
        translations.patientForms.medicalForm.sectionSix.optionPublicTransport,
      value: TRANSPORTATION_TYPE.PUBLIC,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.car,
      value: TRANSPORTATION_TYPE.CAR,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.taxi,
      subForm: () => (
        <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
          fieldName="transport_type_taxi_specification"
          formName={formName}
          isIconOnly
          sourceInformation={sourceInformation}
        >
          <ControlledRadioGroupWithFocus
            aria-label={
              translations.patientForms.medicalForm.sectionSix.taxiOptionLabel
            }
            isRequired={!skipRequiredValidation}
            name="transport_type_taxi_specification"
            options={taxiOptions}
            size="md"
          />
        </SourceWrapper>
      ),
      value: TRANSPORTATION_TYPE.TAXI,
    },
    {
      label: translations.patientForms.medicalForm.sectionSix.ambulance,
      subForm: () => (
        <>
          <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
            fieldName="transport_type_ambulance_specification"
            formName={formName}
            isIconOnly
            sourceInformation={sourceInformation}
          >
            <ControlledRadioGroupWithFocus
              aria-label={
                translations.patientForms.medicalForm.sectionSix.taxiOptionLabel
              }
              isRequired={!skipRequiredValidation}
              name="transport_type_ambulance_specification"
              options={taxiOptions}
              size="md"
            />
          </SourceWrapper>
          <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
            fieldName="transport_type_ambulance_description"
            formName={formName}
            isIconOnly
            sourceInformation={sourceInformation}
          >
            <ControlledTextarea
              label={
                translations.patientForms.medicalForm.sectionSix
                  .ambulanceDescriptionLabel
              }
              name="transport_type_ambulance_description"
            />
          </SourceWrapper>
        </>
      ),
      value: TRANSPORTATION_TYPE.AMBULANCE,
    },
  ];

  return (
    <ControlledRadioGroupWithFocus
      aria-label={
        translations.patientForms.medicalForm.sectionSix.transportation
      }
      isRequired={!skipRequiredValidation}
      name="transport_type"
      options={options}
      size="md"
    />
  );
};

function getInitialICDCodeFormCount(
  formData: Partial<GKVMedicalFormData> | undefined,
): number {
  if (!formData) return FUNCTION_DIAGNOSIS_PRIORITIES.PRIO_1;

  let maxFilledIndex = 0;

  Object.values(FUNCTION_DIAGNOSIS_PRIORITIES).forEach((priority) => {
    const code =
      formData[`icd_code_${priority}_code` as keyof GKVMedicalFormData];

    if (code) {
      maxFilledIndex = priority;
    }
  });

  return Math.max(maxFilledIndex, FUNCTION_DIAGNOSIS_PRIORITIES.PRIO_1);
}

export const ICDCodeFields = ({
  formName,
  skipRequiredValidation,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const icdReasonOptions = useICDReasonOptions();
  const sideLocalizationOptions = useSideLocalizationOptions();
  const { formState, getValues, setValue } =
    useFormContext<GKVMedicalFormData>();

  const watchedCodes = useWatch({
    name: Object.values(FUNCTION_DIAGNOSIS_PRIORITIES).map(
      (p) => `icd_code_${p}_code` as keyof GKVMedicalFormData,
    ),
  });

  const formCount = getInitialICDCodeFormCount(
    formState.defaultValues as GKVMedicalFormData | undefined,
  );
  const prevFormCountRef = useRef(formCount);

  const {
    canDecrement,
    canIncrement,
    count: visibleCount,
    decrement,
    increment,
    setVisibleCount,
  } = useVisibleCount({
    initialCount: formCount,
    maxCount: FUNCTION_DIAGNOSIS_PRIORITIES.PRIO_5,
    minCount: FUNCTION_DIAGNOSIS_PRIORITIES.PRIO_1,
  });

  useEffect(() => {
    const count = getInitialICDCodeFormCount(getValues());
    const prev = prevFormCountRef.current;
    prevFormCountRef.current = count;
    const shouldExpand = count > visibleCount;
    const shouldShrink = count < visibleCount && count < prev;

    if (shouldExpand || shouldShrink) {
      setVisibleCount(count);
    }
  }, [watchedCodes, getValues, visibleCount, setVisibleCount]);

  const deleteRow = useCallback(
    (priority: number) => {
      const values = getValues();
      for (let i = priority; i < visibleCount; i++) {
        const next = i + 1;
        setValue(
          `icd_code_${i}_diagnosis` as keyof GKVMedicalFormData,
          values[`icd_code_${next}_diagnosis` as keyof GKVMedicalFormData] ??
            "",
          { shouldDirty: true },
        );
        setValue(
          `icd_code_${i}_code` as keyof GKVMedicalFormData,
          values[`icd_code_${next}_code` as keyof GKVMedicalFormData] ?? null,
          { shouldDirty: true },
        );
        setValue(
          `icd_code_${i}_reason` as keyof GKVMedicalFormData,
          values[`icd_code_${next}_reason` as keyof GKVMedicalFormData] ?? null,
          { shouldDirty: true },
        );
        setValue(
          `icd_code_${i}_side_localization_primary` as keyof GKVMedicalFormData,
          values[
            `icd_code_${next}_side_localization_primary` as keyof GKVMedicalFormData
          ] ?? null,
          { shouldDirty: true },
        );
      }
      setValue(
        `icd_code_${visibleCount}_diagnosis` as keyof GKVMedicalFormData,
        "",
        { shouldDirty: true },
      );
      setValue(
        `icd_code_${visibleCount}_code` as keyof GKVMedicalFormData,
        null,
        { shouldDirty: true },
      );
      setValue(
        `icd_code_${visibleCount}_reason` as keyof GKVMedicalFormData,
        null,
        { shouldDirty: true },
      );
      setValue(
        `icd_code_${visibleCount}_side_localization_primary` as keyof GKVMedicalFormData,
        null,
        { shouldDirty: true },
      );
      decrement();
    },
    [decrement, getValues, setValue, visibleCount],
  );

  return (
    <div className="flex w-full flex-col gap-2">
      {Object.values(FUNCTION_DIAGNOSIS_PRIORITIES).map((priority) => {
        return (
          <div
            key={priority}
            className={clsx("flex w-full flex-col gap-2", {
              hidden: priority > visibleCount,
            })}
          >
            <div className="flex w-full flex-col justify-between">
              <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
                fieldName={`icd_code_${priority}_diagnosis`}
                formName={formName}
                sourceInformation={sourceInformation}
              >
                <ControlledInput
                  isRequired={
                    priority === FUNCTION_DIAGNOSIS_PRIORITIES.PRIO_1 &&
                    !skipRequiredValidation
                  }
                  label={
                    translations.patientForms.medicalForm.sectionTwo
                      .diagnosisDescription
                  }
                  name={`icd_code_${priority}_diagnosis`}
                />
              </SourceWrapper>
              <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
                fieldName={`icd_code_${priority}_code`}
                formName={formName}
                sourceInformation={sourceInformation}
              >
                <ControlledAlgoliaSelect
                  algoliaAnalyticsName={ALGOLIA_ASSESSMENT_ICD_CODES}
                  elementName={`icd_code_${priority}_code`}
                  indexName="icd_codes"
                  isRequired={
                    priority === FUNCTION_DIAGNOSIS_PRIORITIES.PRIO_1 &&
                    !skipRequiredValidation
                  }
                  label={
                    translations.patientForms.medicalForm.sectionTwo.icdCode
                  }
                  name={`icd_code_${priority}_code`}
                  placeholder={
                    translations.general.selectDefaultPlaceholderShort
                  }
                  renderHits={renderIcdHit}
                  shouldSearchOnInitialRender
                  shouldTruncateMenuItems
                  withPortal
                />
                <span className="mt-2 text-xs text-gray-600">
                  {translations.patientForms.medicalForm.sectionTwo.footnoteOne}
                </span>
              </SourceWrapper>
              <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
                fieldName={`icd_code_${priority}_reason`}
                formName={formName}
                sourceInformation={sourceInformation}
              >
                <ControlledSelect
                  isRequired={priority === 1 && !skipRequiredValidation}
                  items={icdReasonOptions}
                  label={translations.patientForms.medicalForm.sectionTwo.cause}
                  name={`icd_code_${priority}_reason`}
                  renderItem={(item) => item.label}
                  required={priority === 1 && !skipRequiredValidation}
                />
              </SourceWrapper>
              <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
                fieldName={`icd_code_${priority}_reason`}
                formName={formName}
                sourceInformation={sourceInformation}
              >
                <ControlledSelect
                  items={sideLocalizationOptions}
                  label={
                    translations.patientForms.medicalForm.sectionTwo
                      .sideLocalization
                  }
                  name={`icd_code_${priority}_side_localization_primary`}
                  renderItem={(item) => item.label}
                  required={priority === 1 && !skipRequiredValidation}
                />
              </SourceWrapper>
              {canDecrement && (
                <div className="mt-2 px-4">
                  <Button
                    color="danger"
                    id={`delete-priority-${priority}`}
                    isDisabled={!canDecrement}
                    startContent={<TrashIcon className="h-4 w-4" />}
                    variant="light"
                    onPress={() => deleteRow(priority)}
                  >
                    {
                      translations.patientForms.medicalForm.sectionTwo
                        .deleteDiagnosis
                    }
                  </Button>
                </div>
              )}
              {canDecrement && <hr className="mt-6 mb-4 border-gray-400" />}
            </div>
            {!canDecrement && <hr className="my-4 border-gray-400" />}
            {priority >= visibleCount && (
              <div className="flex w-full justify-between pl-4">
                <Button
                  color="primary"
                  id={`add-more-priority-${priority}`}
                  isDisabled={!canIncrement}
                  startContent={<PlusIcon className="h-4 w-4" />}
                  variant="ghost"
                  onPress={increment}
                >
                  {
                    translations.patientForms.medicalForm.sectionTwo
                      .addDiagnosis
                  }
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const DEFAULT_VISIBLE_OPERATION_ROWS = 1;

function getInitialOperationFormCount(
  formData: GKVMedicalFormData | undefined,
): number {
  let maxFilledIndex = DEFAULT_VISIBLE_OPERATION_ROWS;

  Object.values(OPERATION_INDEXES).forEach((opsIndex) => {
    const date =
      formData?.[`operation_${opsIndex}_date` as keyof GKVMedicalFormData];

    const ops = (
      formData?.[`operation_${opsIndex}_ops` as keyof GKVMedicalFormData] as
        | string[]
        | undefined
    )?.some((op) => op !== "");

    const woundClosed =
      formData?.[
        `operation_${opsIndex}_wound_closed` as keyof GKVMedicalFormData
      ];

    if (date || woundClosed || ops) {
      maxFilledIndex = opsIndex;
    }
  });

  return Math.max(maxFilledIndex, DEFAULT_VISIBLE_OPERATION_ROWS);
}

function getInitialOperationRows(fieldValue: string[] | undefined): number {
  let maxFilledIndex = DEFAULT_VISIBLE_OPERATION_ROWS;

  fieldValue?.forEach((op, index) => {
    if (op !== "") {
      maxFilledIndex = index + 1;
    }
  });

  return Math.max(maxFilledIndex, DEFAULT_VISIBLE_OPERATION_ROWS);
}

type OpsDisplayState = {
  visibleCount: number;
  opsVisibleCount: Record<number, number>;
};

function getInitialDisplayState(
  formData: GKVMedicalFormData | undefined,
): OpsDisplayState {
  const opsVisibleCount: Record<number, number> = {};
  Object.values(OPERATION_INDEXES).forEach((opsIndex) => {
    const ops = formData?.[
      `operation_${opsIndex}_ops` as keyof GKVMedicalFormData
    ] as string[] | undefined;
    opsVisibleCount[opsIndex] = getInitialOperationRows(
      Array.isArray(ops) ? ops : undefined,
    );
  });
  return {
    opsVisibleCount,
    visibleCount: getInitialOperationFormCount(formData),
  };
}

function OpsFields({
  formName,
  onAddOps,
  onClear,
  onOpsChange,
  onRemoveOpsAt,
  ops,
  opsIndex,
  opsVisibleCount,
  prefix,
  shouldClearValue,
  sourceInformation,
  translations,
}: {
  ops: string[];
  opsIndex: number;
  opsVisibleCount: number;
  onAddOps: () => void;
  onClear: () => void;
  onOpsChange: (ops: string[]) => void;
  onRemoveOpsAt: (index: number) => void;
  prefix: string;
  formName: FormFieldConfigProps<
    typeof DOCS_FORM_NAMES.GKV_MEDICAL
  >["formName"];
  shouldClearValue: boolean;
  sourceInformation: FormFieldConfigProps<
    typeof DOCS_FORM_NAMES.GKV_MEDICAL
  >["sourceInformation"];
  translations: Translations;
}) {
  const fieldName = `${prefix}_ops` as keyof GKVMedicalFormData;

  const { setValue } = useFormContext<GKVMedicalFormData>();

  const onClearRef = useRef(onClear);
  onClearRef.current = onClear;

  useEffect(() => {
    if (shouldClearValue) {
      setValue(fieldName, DEFAULT_OPS);
      onClearRef.current();
    }
  }, [shouldClearValue, setValue, fieldName]);

  const canDecrement = opsVisibleCount > DEFAULT_VISIBLE_OPERATION_ROWS;
  const canIncrement = opsVisibleCount < NUMBER_OF_OPS_FIELDS;

  const removeOpsCodeField = (index: number) => {
    onRemoveOpsAt(index);
  };

  return (
    <div className="flex flex-col gap-2">
      <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
        fieldName={fieldName}
        formName={formName}
        isIconOnly
        sourceInformation={sourceInformation}
      >
        <div className="flex flex-col gap-2">
          {DEFAULT_OPS.map((_, index) => {
            const opsFieldIndex = index + 1;
            return (
              <div
                key={opsFieldIndex}
                className={clsx("flex w-full items-end gap-2", {
                  hidden: opsFieldIndex > opsVisibleCount,
                })}
              >
                <Input
                  label={`${translations.patientForms.medicalForm.sectionTwo.surgeryOps} ${opsFieldIndex}`}
                  value={ops[index]}
                  onValueChange={(value) => {
                    const newOps = [...ops];
                    newOps[index] = value;
                    onOpsChange(newOps);
                  }}
                />
                {canDecrement && (
                  <Button
                    aria-label={translations.actions.remove}
                    color="danger"
                    id={`operation-${opsIndex}-remove-ops-code-field-${index}`}
                    isIconOnly
                    size="sm"
                    startContent={<TrashIcon className="h-4 w-4" />}
                    variant="light"
                    onPress={() => removeOpsCodeField(index)}
                  />
                )}
              </div>
            );
          })}
        </div>
      </SourceWrapper>
      <div className="flex w-full pl-4">
        <Button
          color="primary"
          id={`operation-${opsIndex}-add-ops-code-field`}
          isDisabled={!canIncrement}
          startContent={<PlusIcon className="h-4 w-4" />}
          variant="ghost"
          onPress={onAddOps}
        >
          {translations.patientForms.medicalForm.sectionTwo.addOpsCode}
        </Button>
      </div>
    </div>
  );
}

function OperationsSubForm({
  formName,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) {
  const { formState, getValues, setValue } =
    useFormContext<GKVMedicalFormData>();

  const surgeryPerformed = useWatch<GKVMedicalFormData>({
    defaultValue: formState.defaultValues?.surgery_performed as
      | PredicamentYesNo
      | undefined,
    name: "surgery_performed",
  });

  const [displayState, setDisplayState] = useState<OpsDisplayState>(() =>
    getInitialDisplayState(
      formState.defaultValues as GKVMedicalFormData | undefined,
    ),
  );
  const { opsVisibleCount, visibleCount: count } = displayState;

  const opsFieldNames = useMemo(
    () =>
      Object.values(OPERATION_INDEXES).map(
        (i) => `operation_${i}_ops` as keyof GKVMedicalFormData,
      ),
    [],
  );

  const operationDateFieldNames = useMemo(
    () =>
      Object.values(OPERATION_INDEXES).map(
        (i) => `operation_${i}_date` as keyof GKVMedicalFormData,
      ),
    [],
  );
  const woundClosedFieldNames = useMemo(
    () =>
      Object.values(OPERATION_INDEXES).map(
        (i) => `operation_${i}_wound_closed` as keyof GKVMedicalFormData,
      ),
    [],
  );

  const watchedOps = useWatch({ name: opsFieldNames }) as Array<
    string[] | undefined
  >;

  const watchedOperationDates = useWatch({
    name: operationDateFieldNames,
  }) as Array<number | null | undefined>;

  const watchedWoundClosed = useWatch({
    name: woundClosedFieldNames,
  }) as Array<PredicamentYesNo | null | undefined>;

  const effectiveCount = Object.values(OPERATION_INDEXES).reduce((max, i) => {
    const hasDate = watchedOperationDates[i - 1] != null;
    const hasOps = (watchedOps[i - 1] ?? []).some((v) => v !== "");
    const hasWound = watchedWoundClosed[i - 1] != null;
    return hasDate || hasOps || hasWound ? Math.max(max, i) : max;
  }, count);

  const canDecrement = effectiveCount > DEFAULT_VISIBLE_OPERATION_ROWS;
  const canIncrement = effectiveCount < OPERATION_INDEXES.OP5;

  const increment = useCallback(() => {
    setDisplayState((prev) => {
      const nextCount = Math.min(effectiveCount + 1, OPERATION_INDEXES.OP5);
      return {
        opsVisibleCount: {
          ...prev.opsVisibleCount,
          [nextCount]: DEFAULT_VISIBLE_OPERATION_ROWS,
        },
        visibleCount: nextCount,
      };
    });
  }, [effectiveCount]);

  const removeOperation = useCallback(
    (indexToRemove: number) => {
      if (indexToRemove === effectiveCount) {
        setValue(
          `operation_${indexToRemove}_date` as keyof GKVMedicalFormData,
          null,
          { shouldDirty: true },
        );
        setValue(
          `operation_${indexToRemove}_wound_closed` as keyof GKVMedicalFormData,
          null,
          { shouldDirty: true },
        );
        setValue(
          `operation_${indexToRemove}_ops` as keyof GKVMedicalFormData,
          DEFAULT_OPS as string[],
          { shouldDirty: true },
        );
        setDisplayState((prev) => ({
          opsVisibleCount: {
            ...prev.opsVisibleCount,
            [indexToRemove]: DEFAULT_VISIBLE_OPERATION_ROWS,
          },
          visibleCount: Math.max(
            prev.visibleCount - 1,
            DEFAULT_VISIBLE_OPERATION_ROWS,
          ),
        }));
        return;
      }
      const values = getValues();
      for (let i = indexToRemove; i < effectiveCount; i++) {
        const next = i + 1;
        setValue(
          `operation_${i}_date` as keyof GKVMedicalFormData,
          values[`operation_${next}_date` as keyof GKVMedicalFormData] ?? null,
          { shouldDirty: true },
        );
        setValue(
          `operation_${i}_wound_closed` as keyof GKVMedicalFormData,
          values[
            `operation_${next}_wound_closed` as keyof GKVMedicalFormData
          ] ?? null,
          { shouldDirty: true },
        );
        setValue(
          `operation_${i}_ops` as keyof GKVMedicalFormData,
          (values[`operation_${next}_ops` as keyof GKVMedicalFormData] ??
            DEFAULT_OPS) as string[],
          { shouldDirty: true },
        );
      }
      setValue(
        `operation_${effectiveCount}_date` as keyof GKVMedicalFormData,
        null,
        { shouldDirty: true },
      );
      setValue(
        `operation_${effectiveCount}_wound_closed` as keyof GKVMedicalFormData,
        null,
        { shouldDirty: true },
      );
      setValue(
        `operation_${effectiveCount}_ops` as keyof GKVMedicalFormData,
        DEFAULT_OPS as string[],
        { shouldDirty: true },
      );
      setDisplayState((prev) => {
        const nextOpsVisibleCount = { ...prev.opsVisibleCount };
        for (let i = indexToRemove; i < effectiveCount; i++) {
          nextOpsVisibleCount[i] =
            prev.opsVisibleCount[i + 1] ?? DEFAULT_VISIBLE_OPERATION_ROWS;
        }
        nextOpsVisibleCount[effectiveCount] = DEFAULT_VISIBLE_OPERATION_ROWS;
        return {
          opsVisibleCount: nextOpsVisibleCount,
          visibleCount: Math.max(
            prev.visibleCount - 1,
            DEFAULT_VISIBLE_OPERATION_ROWS,
          ),
        };
      });
    },
    [effectiveCount, getValues, setValue],
  );

  useEffect(() => {
    for (let i = effectiveCount + 1; i <= OPERATION_INDEXES.OP5; i++) {
      setValue(`operation_${i}_date` as keyof GKVMedicalFormData, null);
      setValue(`operation_${i}_wound_closed` as keyof GKVMedicalFormData, null);
    }
  }, [effectiveCount, setValue]);

  return (
    <div className="flex w-full flex-col">
      {Object.values(OPERATION_INDEXES).map((opsIndex) => {
        const prefix = `operation_${opsIndex}`;
        const currentOps = watchedOps[opsIndex - 1] ?? DEFAULT_OPS;
        const effectiveOpsVisibleCount = Math.max(
          opsVisibleCount[opsIndex] ?? DEFAULT_VISIBLE_OPERATION_ROWS,
          getInitialOperationRows(currentOps),
        );
        return (
          <div
            key={opsIndex}
            className={clsx("flex w-full flex-col gap-2", {
              hidden: opsIndex > effectiveCount,
            })}
          >
            <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
              fieldName={`${prefix}_date` as keyof GKVMedicalFormData}
              formName={formName}
              isIconOnly
              sourceInformation={sourceInformation}
            >
              <ControlledDatePicker
                label={
                  translations.patientForms.medicalForm.sectionTwo.surgeryDate
                }
                name={`${prefix}_date`}
              />
            </SourceWrapper>
            <OpsFields
              formName={formName}
              ops={currentOps}
              opsIndex={opsIndex}
              opsVisibleCount={effectiveOpsVisibleCount}
              prefix={prefix}
              shouldClearValue={
                surgeryPerformed !== PREDICAMENT_STATE_YES ||
                opsIndex > effectiveCount
              }
              sourceInformation={sourceInformation}
              translations={translations}
              onAddOps={() =>
                setDisplayState((prev) => ({
                  ...prev,
                  opsVisibleCount: {
                    ...prev.opsVisibleCount,
                    [opsIndex]: Math.min(
                      effectiveOpsVisibleCount + 1,
                      NUMBER_OF_OPS_FIELDS,
                    ),
                  },
                }))
              }
              onClear={() =>
                setDisplayState((prev) => ({
                  ...prev,
                  opsVisibleCount: {
                    ...prev.opsVisibleCount,
                    [opsIndex]: DEFAULT_VISIBLE_OPERATION_ROWS,
                  },
                }))
              }
              onOpsChange={(newOps) =>
                setValue(
                  `operation_${opsIndex}_ops` as keyof GKVMedicalFormData,
                  newOps,
                  { shouldDirty: true },
                )
              }
              onRemoveOpsAt={(index) => {
                const newOps = [...currentOps];
                newOps.splice(index, 1);
                newOps.push("");
                setValue(
                  `operation_${opsIndex}_ops` as keyof GKVMedicalFormData,
                  newOps,
                  { shouldDirty: true },
                );
                setDisplayState((prev) => ({
                  ...prev,
                  opsVisibleCount: {
                    ...prev.opsVisibleCount,
                    [opsIndex]: Math.max(
                      effectiveOpsVisibleCount - 1,
                      DEFAULT_VISIBLE_OPERATION_ROWS,
                    ),
                  },
                }));
              }}
            />
            <SourceWrapper<typeof DOCS_FORM_NAMES.GKV_MEDICAL>
              fieldName={`${prefix}_wound_closed` as keyof GKVMedicalFormData}
              formName={formName}
              isIconOnly
              sourceInformation={sourceInformation}
            >
              <ControlledRadioGroup
                label={translations.patientForms.medicalForm.sectionTwo.wound}
                name={`${prefix}_wound_closed`}
                options={[
                  {
                    label: translations.actions.no,
                    value: PREDICAMENT_STATE_NO,
                  },
                  {
                    label: translations.actions.yes,
                    value: PREDICAMENT_STATE_YES,
                  },
                ]}
                size="md"
              />
            </SourceWrapper>
            {canDecrement && (
              <div>
                <Button
                  color="danger"
                  id={`remove-operation-field-${opsIndex}`}
                  startContent={<TrashIcon className="h-4 w-4" />}
                  variant="light"
                  onPress={() => removeOperation(opsIndex)}
                >
                  {
                    translations.patientForms.drvMedicalForm.sectionTwo
                      .deleteOperations
                  }
                </Button>
                <hr className="my-6 border-gray-400" />
              </div>
            )}
          </div>
        );
      })}
      {!canDecrement && <hr className="my-6 border-gray-400" />}
      <div className="flex pl-4">
        <Button
          color="primary"
          id="add-operation-field"
          isDisabled={!canIncrement}
          startContent={<PlusIcon className="h-4 w-4" />}
          variant="ghost"
          onPress={increment}
        >
          {translations.patientForms.medicalForm.sectionTwo.addAnotherOperation}
        </Button>
      </div>
    </div>
  );
}

export const SurgeryPerformedField = ({
  formName,
  skipRequiredValidation,
  sourceInformation,
  translations,
}: FormFieldConfigProps<typeof DOCS_FORM_NAMES.GKV_MEDICAL>) => {
  const { formState, setValue } = useFormContext<GKVMedicalFormData>();
  const surgeryPerformed = useWatch<GKVMedicalFormData>({
    defaultValue: formState.defaultValues?.surgery_performed as
      | PredicamentYesNo
      | undefined,
    name: "surgery_performed",
  });

  useEffect(() => {
    if (surgeryPerformed !== PREDICAMENT_STATE_YES) {
      Object.values(OPERATION_INDEXES).forEach((opsIndex) => {
        const prefix = `operation_${opsIndex}`;
        setValue(`${prefix}_date` as keyof GKVMedicalFormData, null);
        setValue(`${prefix}_wound_closed` as keyof GKVMedicalFormData, null);
        setValue(`${prefix}_ops` as keyof GKVMedicalFormData, DEFAULT_OPS);
      });
    }
  }, [surgeryPerformed, setValue]);

  const options = [
    {
      label: translations.actions.no,
      value: PREDICAMENT_STATE_NO,
    },
    {
      label: translations.actions.yes,
      subForm: () => (
        <OperationsSubForm
          formName={formName}
          skipRequiredValidation={skipRequiredValidation}
          sourceInformation={sourceInformation}
          translations={translations}
        />
      ),
      value: PREDICAMENT_STATE_YES,
    },
  ];

  return (
    <ControlledRadioGroupWithFocus
      aria-label={
        translations.patientForms.medicalForm.sectionTwo.performedTreatments
      }
      className="w-full"
      isRequired={!skipRequiredValidation}
      name="surgery_performed"
      options={options}
      size="md"
    />
  );
};

export function SingerAssessmentSectionTitle() {
  const translations = useTranslations();
  const watchedValues = useWatch({ name: ALL_SINGER_ASSESSMENT_FIELDS });
  const score = watchedValues.reduce(
    (sum: number, value) => sum + (typeof value === "number" ? value : 0),
    0,
  );

  return (
    <h2 className="flex w-full items-center justify-between px-4 py-4 text-heading-lg font-bold">
      <span>{translations.patient.medicalDiagnosis.singerPatientProfil}</span>
      <span className="flex gap-1">
        <span>
          <span>
            {
              translations.patient.medicalDiagnosis.singerPatientProfile
                .scoreTotalScore
            }
          </span>
          <span>{translations.general.colon}</span>
        </span>
        <span>{score}</span>
      </span>
    </h2>
  );
}

export const SINGER_DOMAIN_SCORE_FIELDS: Record<
  string,
  (keyof GKVMedicalFormData)[]
> = {
  communication: ["singer_assessment_listening", "singer_assessment_talking"],
  domesticLife: ["singer_assessment_domestic_life_household"],
  learn: ["singer_assessment_reading", "singer_assessment_writing"],
  mobility: [
    "singer_assessment_transfer",
    "singer_assessment_wheelchair",
    "singer_assessment_walking",
    "singer_assessment_stairs",
  ],
  selfsufficiency: [
    "singer_assessment_food",
    "singer_assessment_dressing_and_undressing",
    "singer_assessment_hygiene",
    "singer_assessment_shower",
    "singer_assessment_stool",
    "singer_assessment_urination",
    "singer_assessment_toilet_use",
  ],
  socialInteraction: ["singer_assessment_social_interaction"],
  task: [
    "singer_assessment_orientation",
    "singer_assessment_concentration",
    "singer_assessment_planning",
  ],
};

const ALL_SINGER_ASSESSMENT_FIELDS = Object.values(
  SINGER_DOMAIN_SCORE_FIELDS,
).flat();

function getSingerSectionScore(values: RadioGroupProps["options"]): number {
  return (values ?? []).reduce<number>(
    (sum, value) => sum + (typeof value === "number" ? value : 0),
    0,
  );
}

function addLineBreakBeforeBracket(input: string): ReactNode {
  const parts = input.split("(");
  return (
    <>
      {parts[0]}
      {parts.slice(1).map((part, index) => (
        <Fragment key={index}>
          <br />({part}
        </Fragment>
      ))}
    </>
  );
}

function SingerAssessmentLegend() {
  const translations = useTranslations();
  const labels = [
    translations.patient.medicalDiagnosis.singerPatientProfile
      .scoreProfessionalHelp,
    translations.patient.medicalDiagnosis.singerPatientProfile.scoreSupporter,
    translations.patient.medicalDiagnosis.singerPatientProfile.scoreIndependent,
  ];
  const radioLabels = [
    translations.patient.medicalDiagnosis.singerPatientProfile
      .scoreProfessionalHelpZero,
    translations.patient.medicalDiagnosis.singerPatientProfile
      .scoreProfessionalHelpOne,
    translations.patient.medicalDiagnosis.singerPatientProfile
      .scoreSupporterTwo,
    translations.patient.medicalDiagnosis.singerPatientProfile
      .scoreSupporterThree,
    translations.patient.medicalDiagnosis.singerPatientProfile
      .scoreIndependentFour,
    translations.patient.medicalDiagnosis.singerPatientProfile
      .scoreIndependentFive,
  ];

  return (
    <div className="-mx-4 mt-2 grid grid-cols-6 border-y border-gray-400 bg-white">
      {labels.map((label, i) => (
        <p
          key={i}
          className={clsx(
            "col-span-2 border-b border-gray-200 p-2 text-center text-body-md font-bold",
            {
              "border-r border-gray-200": i < labels.length - 1,
            },
          )}
          data-testid={`radio-column-${kebabCase(label)}`}
        >
          {label}
        </p>
      ))}
      {radioLabels.map((label, i) => (
        <p
          key={i}
          className={clsx("m-0 p-2 text-center text-body-md font-normal", {
            "border-r border-gray-200": i < radioLabels.length - 1,
          })}
          data-testid={`radio-label-${i}`}
        >
          {addLineBreakBeforeBracket(label)}
        </p>
      ))}
    </div>
  );
}

export function SingerAssessmentSectionSubTitle({
  scoreFieldNames,
  subtitle,
}: {
  scoreFieldNames: (keyof GKVMedicalFormData)[];
  subtitle: string;
}) {
  const watchedValues = useWatch({ name: scoreFieldNames });
  const sectionScore = getSingerSectionScore(watchedValues);
  const translations = useTranslations();

  return (
    <div>
      {scoreFieldNames === SINGER_DOMAIN_SCORE_FIELDS.selfsufficiency ? (
        <SingerAssessmentLegend />
      ) : (
        <hr className="my-2 border-gray-400" />
      )}
      <h3 className="flex w-full justify-between px-4 py-4 text-heading-md font-bold">
        <span>
          <span>{subtitle}</span>
          <span>{translations.general.colon}</span>
        </span>
        <span>{sectionScore}</span>
      </h3>
    </div>
  );
}

export const getSingerRadioOptions = (
  translations: Translations,
): RadioGroupProps<number>["options"] => {
  return [
    {
      "aria-label":
        translations.patient.medicalDiagnosis.singerPatientProfile
          .scoreProfessionalHelpZero,
      label: "0",
      value: 0,
    },
    {
      "aria-label":
        translations.patient.medicalDiagnosis.singerPatientProfile
          .scoreProfessionalHelpOne,
      label: "1",
      value: 1,
    },
    {
      "aria-label":
        translations.patient.medicalDiagnosis.singerPatientProfile
          .scoreSupporterTwo,
      label: "2",
      value: 2,
    },
    {
      "aria-label":
        translations.patient.medicalDiagnosis.singerPatientProfile
          .scoreSupporterThree,
      label: "3",
      value: 3,
    },
    {
      "aria-label":
        translations.patient.medicalDiagnosis.singerPatientProfile
          .scoreIndependentFour,
      label: "4",
      value: 4,
    },

    {
      "aria-label":
        translations.patient.medicalDiagnosis.singerPatientProfile
          .scoreIndependentFive,
      label: "5",
      value: 5,
    },
  ];
};
